const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toArray = (value) => Array.isArray(value) ? value.filter(Boolean) : [];

const toAmountNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (typeof value === 'string') {
        const parsedValue = parseFloat(value.replace(/,/g, '').trim());
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const mergeDefined = (...sources) => {
    const result = {};

    sources.forEach((source) => {
        if (!isObject(source)) return;

        Object.entries(source).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                result[key] = value;
            }
        });
    });

    return result;
};

const getResolvedTaxRows = (merged = {}, packageDetails = {}) => {
    if (Array.isArray(merged?.taxes)) return merged.taxes;
    if (Array.isArray(merged?.tax_details)) return merged.tax_details;
    if (Array.isArray(merged?.pricingSnapshot?.taxBreakdown)) return merged.pricingSnapshot.taxBreakdown;
    if (Array.isArray(merged?.pricing_snapshot?.tax_breakdown)) return merged.pricing_snapshot.tax_breakdown;
    if (Array.isArray(packageDetails?.taxes)) return packageDetails.taxes;
    if (Array.isArray(packageDetails?.tax_details)) return packageDetails.tax_details;
    return [];
};

const getDerivedTaxRate = (taxRows = []) => {
    return taxRows.reduce((sum, tax) => {
        return sum + toAmountNumber(tax?.tax_rate ?? tax?.rate ?? tax?.percentage ?? tax?.percent ?? 0);
    }, 0);
};

const getDerivedTaxAmount = (taxRows = []) => {
    return taxRows.reduce((sum, tax) => {
        return sum + toAmountNumber(tax?.tax_amount ?? tax?.amount ?? 0);
    }, 0);
};

const getDerivedTaxNames = (taxRows = []) => {
    const names = taxRows
        .map((tax) => String(tax?.tax_name ?? tax?.name ?? tax?.label ?? '').trim())
        .filter(Boolean);

    return names.join(', ');
};

const isActivePackage = (pkg = {}) => {
    const status = String(pkg?.status ?? pkg?.package_status ?? '').toLowerCase();
    const activeFlag = pkg?.is_active ?? pkg?.active ?? pkg?.isActive;

    return activeFlag === true ||
        activeFlag === 1 ||
        activeFlag === '1' ||
        status === 'active' ||
        status === 'current';
};

export const getPackageRows = (source = {}) => {
    const root = isObject(source?.data) ? source.data : source;

    return [
        ...toArray(root?.packages),
        ...toArray(root?.package_list),
        ...toArray(root?.plans),
        ...toArray(root?.subscriptions),
    ].filter(isObject);
};

export const getPrimaryPackageRecord = (source = {}) => {
    const rows = getPackageRows(source);
    return rows.find(isActivePackage) ?? rows[0] ?? null;
};

export const resolveCurrentPackage = (source = {}, packageDetails = {}) => {
    const root = isObject(source?.data) ? source.data : source;
    const entity = isObject(root?.entity) ? root.entity : {};
    const packageRecord = getPrimaryPackageRecord(root);
    const entityPlan = entity?.current_plan ?? entity?.current_package ?? entity?.active_plan ?? entity?.plan ?? {};
    const directPlan = root?.current_package ??
        root?.package_details ??
        root?.current_plan ??
        root?.active_plan ??
        root?.activePlan ??
        root?.plan ??
        root?.package ??
        root?.active_plan_details ??
        {};

    const merged = mergeDefined(packageDetails, entity, packageRecord, entityPlan, directPlan);
    const resolvedTaxRows = getResolvedTaxRows(merged, packageDetails);
    const derivedTaxRate = getDerivedTaxRate(resolvedTaxRows);
    const derivedTaxAmount = getDerivedTaxAmount(resolvedTaxRows);
    const derivedTaxNames = getDerivedTaxNames(resolvedTaxRows);

    return {
        ...merged,
        package_id: merged?.package_id ?? merged?.self_service_plan_id ?? merged?.plan_id ?? merged?.id ?? packageRecord?.package_id ?? packageDetails?.package_id ?? null,
        entity_package_id: merged?.entity_package_id ?? packageRecord?.entity_package_id ?? null,
        package_name: merged?.package_name ?? merged?.plan_name ?? merged?.name ?? packageDetails?.package_name ?? null,
        package_type: merged?.package_type ?? merged?.plan_type ?? (merged?.is_self_service_plan ? 'self_service' : null) ?? entity?.package_type ?? packageRecord?.package_type ?? null,
        taxes: resolvedTaxRows,
        tax_details: resolvedTaxRows,
        total_tax_rate: merged?.total_tax_rate ?? merged?.tax_percentage ?? packageDetails?.total_tax_rate ?? packageDetails?.tax_percentage ?? derivedTaxRate,
        tax_percentage: merged?.tax_percentage ?? merged?.total_tax_rate ?? packageDetails?.tax_percentage ?? packageDetails?.total_tax_rate ?? derivedTaxRate,
        tax_names: merged?.tax_names ?? merged?.tax_label ?? packageDetails?.tax_names ?? packageDetails?.tax_label ?? derivedTaxNames,
        tax_amount: merged?.tax_amount ?? packageDetails?.tax_amount ?? derivedTaxAmount,
        total_amount: merged?.total_amount ?? merged?.total_amount_paid ?? merged?.pricingSnapshot?.totalAmount ?? merged?.pricing_snapshot?.total_amount ?? packageDetails?.total_amount ?? packageDetails?.total_amount_paid ?? 0,
        total_amount_paid: merged?.total_amount_paid ?? merged?.total_amount ?? merged?.pricingSnapshot?.totalAmount ?? merged?.pricing_snapshot?.total_amount ?? packageDetails?.total_amount_paid ?? packageDetails?.total_amount ?? 0,
        base_amount: merged?.base_amount ?? merged?.pricingSnapshot?.taxableSubtotal ?? merged?.pricing_snapshot?.taxable_subtotal ?? merged?.package_amount ?? packageDetails?.base_amount ?? packageDetails?.package_amount ?? 0,
        minimum_monthly_price: merged?.minimum_monthly_price ?? merged?.pricingSnapshot?.totalAmount ?? merged?.pricing_snapshot?.total_amount ?? packageDetails?.minimum_monthly_price ?? 0,
        remaining_amount: merged?.remaining_amount ?? packageDetails?.remaining_amount ?? Math.max(0, toAmountNumber(merged?.package_amount ?? packageDetails?.package_amount) - toAmountNumber(merged?.amount_used ?? packageDetails?.amount_used)),
        services: merged?.services ??
            merged?.package_service ??
            merged?.package_master_services ??
            merged?.service_pricing ??
            merged?.pricingSnapshot?.serviceFees ??
            merged?.pricing_snapshot?.service_fees ??
            packageDetails?.services ??
            packageDetails?.package_service ??
            packageDetails?.package_master_services ??
            packageDetails?.service_pricing ??
            {},
    };
};

export const hasPackageSnapshot = (source = {}, packageDetails = {}) => {
    const resolved = resolveCurrentPackage(source, packageDetails);
    const services = resolved?.services;

    return Boolean(
        resolved?.package_id ||
        resolved?.package_name ||
        resolved?.entity_package_id ||
        resolved?.total_amount ||
        resolved?.base_amount ||
        resolved?.package_amount ||
        resolved?.amount ||
        (Array.isArray(resolved?.taxes) && resolved.taxes.length > 0) ||
        (isObject(services) && Object.keys(services).length > 0)
    );
};
